import { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getWidget } from '../lib/api';

const AvailabilityPage = () => {
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWidget = async () => {
      try {
        const res = await getWidget('availability');
        setWidget(res.data);
      } catch (error) {
        console.error('Error fetching availability widget:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWidget();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="availability-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4">Book Your Date</p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-tight text-neutral-900 mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Check Availability</h1>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto px-4">
            See if your event date is available and start planning your perfect celebration.
          </p>
        </div>
      </section>

      {/* Widget Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-neutral-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          {widget?.is_active && widget?.code ? (
            <div 
              className="widget-container bg-white p-6 sm:p-8 rounded-lg shadow-sm"
              dangerouslySetInnerHTML={{ __html: widget.code }}
            />
          ) : (
            <div className="text-center py-12 sm:py-16 bg-white rounded-lg">
              <Calendar className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500 text-base sm:text-lg">Availability calendar coming soon!</p>
              <p className="text-neutral-400 text-sm mt-2">Please contact us directly to check availability for your event date.</p>
              <a href="/contact" className="inline-block mt-6 bg-neutral-900 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-neutral-800 transition-colors">
                Contact Us
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Booking Information</h2>
            <div className="space-y-4 text-left">
              <div className="bg-neutral-50 p-4 sm:p-6 rounded-lg">
                <h3 className="font-semibold mb-2">How far in advance should I book?</h3>
                <p className="text-neutral-500 text-sm">We recommend booking at least 2-3 months in advance, especially for peak wedding season (May-October). Popular dates fill up quickly!</p>
              </div>
              <div className="bg-neutral-50 p-4 sm:p-6 rounded-lg">
                <h3 className="font-semibold mb-2">What happens after I check availability?</h3>
                <p className="text-neutral-500 text-sm">Once you find an available date, we'll reach out to discuss your event details, music preferences, and answer any questions you have.</p>
              </div>
              <div className="bg-neutral-50 p-4 sm:p-6 rounded-lg">
                <h3 className="font-semibold mb-2">Can I hold a date temporarily?</h3>
                <p className="text-neutral-500 text-sm">Yes! We can hold your date for up to 7 days while you finalize your plans. A deposit secures your booking.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AvailabilityPage;
